/**
 * Relay client — the browser side of the Dunea Remote protocol (T1 §4/§5).
 * Direct port of the mobile app's lib/relay.ts: outer relay envelopes,
 * identity-sealed hello handshake, ephemeral session keys, fid-acked
 * reliable frames, LRU dedupe. Identity lives in localStorage.
 */
import { listDevices, registerDevice, relayToken, relayUrl } from './api';
import {
  b64,
  createSession,
  generateIdentity,
  newEphemeral,
  openIdentity,
  sealIdentity,
  type KeyPair,
  type SessionCrypto,
} from './crypto';
import { REMOTE_PROTO, REMOTE_PROTO_MIN, type RemoteDevice, type Snapshot } from './protocol';

const KEY_IDENTITY = 'dunea.remote.identity';
const BACKOFF_MIN = 1_000;
const BACKOFF_MAX = 120_000;
const ACK_RETRY_MS = [5_000, 15_000, 45_000];
const RPC_TIMEOUT_MS = 20_000;

export interface DeviceIdentity {
  deviceId: string;
  publicKey: string; // b64
  secretKey: string; // b64
}

interface SidecarPeer {
  deviceId: string;
  name: string;
  longTermKey: Uint8Array;
  eph: KeyPair;
  session: SessionCrypto | null;
  /**
   * We greeted and are waiting for theirs. Tells an ANSWER to our hello apart
   * from a sidecar opening its own handshake — which needs our ephemeral back.
   */
  helloSent: boolean;
  online: boolean;
  outbox: Map<string, { payload: object; attempts: number; timer: ReturnType<typeof setTimeout> | null }>;
  seenFrames: string[];
}

export type RelayEvents = {
  status: (s: { connected: boolean; sidecarsOnline: string[]; handshaken: string[] }) => void;
  snapshot: (sidecarId: string, snap: Snapshot) => void;
  event: (sidecarId: string, event: string, data: unknown) => void;
  stream: (sidecarId: string, d: { sessionId: string; text: string; thinking: string }) => void;
  devices: (devices: RemoteDevice[]) => void;
  decryptFailed: (sidecarId: string) => void;
};

type Listener = { [K in keyof RelayEvents]?: RelayEvents[K][] };

export function loadIdentity(): DeviceIdentity | null {
  const raw = localStorage.getItem(KEY_IDENTITY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as DeviceIdentity;
  } catch {
    return null;
  }
}

export function clearIdentity(): void {
  localStorage.removeItem(KEY_IDENTITY);
}

/** Register this browser as a client device (idempotent per public key). */
export async function ensureIdentity(name: string): Promise<DeviceIdentity> {
  const existing = loadIdentity();
  let publicKey: string;
  let secretKey: string;

  if (existing) {
    publicKey = existing.publicKey;
    secretKey = existing.secretKey;
  } else {
    const kp = generateIdentity();
    publicKey = b64.enc(kp.publicKey);
    secretKey = b64.enc(kp.secretKey);
  }

  const row = await registerDevice(name, publicKey);
  const identity: DeviceIdentity = { deviceId: row.id, publicKey, secretKey };
  localStorage.setItem(KEY_IDENTITY, JSON.stringify(identity));

  return identity;
}

export { listDevices };

export class RelayClient {
  private _ws: WebSocket | null = null;
  private _identity: DeviceIdentity | null = null;
  private _peers = new Map<string, SidecarPeer>();
  private _registry: RemoteDevice[] = [];
  private _backoff = BACKOFF_MIN;
  private _stopped = true;
  private _connected = false;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _listeners: Listener = {};
  private _pendingRpc = new Map<
    string,
    { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }
  >();

  on<K extends keyof RelayEvents>(event: K, fn: RelayEvents[K]): () => void {
    (this._listeners[event] ??= [] as never[]).push(fn as never);

    return () => {
      this._listeners[event] = (this._listeners[event] as never[])?.filter((f) => f !== (fn as never)) as never;
    };
  }

  private _emit<K extends keyof RelayEvents>(event: K, ...args: Parameters<RelayEvents[K]>) {
    for (const fn of this._listeners[event] ?? []) {
      (fn as (...a: unknown[]) => void)(...args);
    }
  }

  private _emitStatus() {
    this._emit('status', {
      connected: this._connected,
      sidecarsOnline: [...this._peers.values()].filter((p) => p.online).map((p) => p.deviceId),
      handshaken: [...this._peers.values()].filter((p) => p.session).map((p) => p.deviceId),
    });
  }

  sidecarName(deviceId: string): string {
    return this._peers.get(deviceId)?.name ?? this._registry.find((d) => d.id === deviceId)?.name ?? 'Mac';
  }

  async start(identity: DeviceIdentity) {
    this._identity = identity;
    this._stopped = false;
    this._backoff = BACKOFF_MIN;
    await this._connect();
  }

  stop() {
    this._stopped = true;

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
    }

    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }

    for (const peer of this._peers.values()) {
      this._teardownPeer(peer);
    }
    this._peers.clear();

    for (const [, p] of this._pendingRpc) {
      clearTimeout(p.timer);
      p.reject(new Error('Disconnected'));
    }
    this._pendingRpc.clear();

    try {
      this._ws?.close(1000, 'client stopping');
    } catch {
      // already closed
    }
    this._ws = null;
    this._connected = false;
    this._emitStatus();
  }

  /** Tab became visible / network back — force a prompt reconnect attempt. */
  nudge() {
    if (this._stopped || this._ws) {
      return;
    }

    this._backoff = BACKOFF_MIN;

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
    }

    void this._connect();
  }

  private async _connect() {
    if (this._stopped || this._ws || !this._identity) {
      return;
    }

    try {
      const token = await relayToken(this._identity.deviceId);
      const ws = new WebSocket(`${relayUrl()}?token=${encodeURIComponent(token)}`);
      this._ws = ws;

      ws.onopen = () => {
        this._backoff = BACKOFF_MIN;
        this._connected = true;
        this._emitStatus();
        this._scheduleTokenRefresh();
      };
      ws.onmessage = (ev) => void this._onEnvelope(String(ev.data));

      ws.onclose = (ev) => {
        if (this._ws === ws) {
          this._ws = null;
          this._connected = false;

          for (const peer of this._peers.values()) {
            this._teardownPeer(peer);
          }
          this._emitStatus();

          if (ev.code === 4401 || ev.code === 4403) {
            return; // token rejected / revoked — the page layer re-pairs
          }

          this._scheduleReconnect();
        }
      };

      ws.onerror = () => {
        // close follows
      };
    } catch {
      this._scheduleReconnect();
    }
  }

  private _scheduleReconnect() {
    if (this._stopped) {
      return;
    }

    const delay = this._backoff + Math.random() * this._backoff * 0.3;
    this._backoff = Math.min(BACKOFF_MAX, this._backoff * 2);

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
    }

    this._reconnectTimer = setTimeout(() => void this._connect(), delay);
  }

  private _scheduleTokenRefresh() {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }

    this._refreshTimer = setTimeout(async () => {
      try {
        if (!this._identity || !this._ws) {
          return;
        }

        const token = await relayToken(this._identity.deviceId);
        this._ws.send(JSON.stringify({ v: 1, kind: 'token-refresh', token }));
      } catch {
        // next refresh rides the reconnect
      }
      this._scheduleTokenRefresh();
    }, 45 * 60_000);
  }

  // ── envelopes ────────────────────────────────────────────────────────────

  private async _onEnvelope(raw: string) {
    let env: Record<string, unknown>;

    try {
      env = JSON.parse(raw);
    } catch {
      return;
    }

    switch (env.kind) {
      case 'registry': {
        this._registry = (env.devices as RemoteDevice[]) ?? [];
        this._emit('devices', this._registry);

        for (const row of this._registry) {
          if (row.role === 'sidecar' && row.state === 'approved' && !this._peers.has(row.id)) {
            this._peers.set(row.id, this._newPeer(row));
          }
        }

        for (const peer of [...this._peers.values()]) {
          const row = this._registry.find((d) => d.id === peer.deviceId);

          if (!row || row.state !== 'approved') {
            this._teardownPeer(peer);
            this._peers.delete(peer.deviceId);
          }
        }
        this._emitStatus();

        return;
      }
      case 'presence': {
        const deviceId = String(env.device ?? '');
        const online = env.online === true;
        const row = this._registry.find((d) => d.id === deviceId);

        if (row?.role !== 'sidecar') {
          return;
        }

        let peer = this._peers.get(deviceId);

        if (!online) {
          if (peer) {
            peer.online = false;
            peer.session = null;
            this._emitStatus();
          }

          return;
        }

        if (!peer && row.state === 'approved') {
          peer = this._newPeer(row);
          this._peers.set(deviceId, peer);
        }

        if (peer) {
          peer.online = true;
          this._initPeer(peer);
        }

        return;
      }
      case 'frame': {
        await this._onFrame(String(env.from ?? ''), String(env.ct ?? ''), String(env.n ?? ''));
        return;
      }
      default:
        return;
    }
  }

  private _newPeer(row: RemoteDevice): SidecarPeer {
    return {
      deviceId: row.id,
      name: row.name,
      longTermKey: b64.dec(row.publicKey),
      eph: newEphemeral(),
      session: null,
      helloSent: false,
      online: row.role === 'sidecar',
      outbox: new Map(),
      seenFrames: [],
    };
  }

  /** Fresh ephemeral + hello — client side of the handshake. */
  private _initPeer(peer: SidecarPeer) {
    if (!this._identity) {
      return;
    }

    peer.eph = newEphemeral();
    peer.session = null;
    peer.helloSent = true;
    this._sendHello(peer);
    this._emitStatus();
  }

  private _sendHello(peer: SidecarPeer) {
    if (!this._identity) {
      return;
    }

    const hello = sealIdentity(
      { t: 'hello', eph: b64.enc(peer.eph.publicKey), proto: REMOTE_PROTO, minProto: REMOTE_PROTO_MIN, ts: Date.now() },
      b64.dec(this._identity.secretKey),
      peer.longTermKey,
    );
    this._sendEnvelope(peer.deviceId, hello);
  }

  private async _onFrame(from: string, ct: string, n: string) {
    if (!this._identity) {
      return;
    }

    const peer = this._peers.get(from);

    if (!peer) {
      return;
    }

    const viaSession = peer.session?.open<Record<string, unknown>>(ct, n) ?? null;

    if (viaSession) {
      this._onPayload(peer, viaSession);
      return;
    }

    const viaIdentity = openIdentity<Record<string, unknown>>(
      ct,
      n,
      peer.longTermKey,
      b64.dec(this._identity.secretKey),
    );

    if (viaIdentity?.t === 'hello') {
      const proto = Number(viaIdentity.proto) || 0;

      if (proto < REMOTE_PROTO_MIN || REMOTE_PROTO < Number(viaIdentity.minProto || 0)) {
        return;
      }

      // Answer unless this is the reply to our own hello. Our ephemeral public
      // key only travels in our hello, so a sidecar that opens the handshake
      // (or re-opens it after its reconnect) cannot derive the session key
      // without one back. Keyed on "did we greet first", not on "do we have a
      // session": the latter withholds the reply in the reconnect case, which
      // leaves the peer talking in a key we can never read.
      const solicited = peer.helloSent;
      peer.helloSent = false;
      peer.session = createSession(peer.eph.secretKey, b64.dec(String(viaIdentity.eph)));
      peer.online = true;

      if (!solicited) {
        this._sendHello(peer);
      }

      this._emitStatus();

      return;
    }

    if (viaIdentity?.t === 'bye') {
      peer.session = null;
      this._emitStatus();

      return;
    }

    this._emit('decryptFailed', from);
  }

  private _onPayload(peer: SidecarPeer, payload: Record<string, unknown>) {
    const frameId = typeof payload.fid === 'string' ? payload.fid : null;

    if (frameId && payload.t !== 'ack') {
      if (peer.seenFrames.includes(frameId)) {
        this._sendSealed(peer, { t: 'ack', ids: [frameId] });
        return;
      }

      peer.seenFrames.push(frameId);

      if (peer.seenFrames.length > 4096) {
        peer.seenFrames.splice(0, 1024);
      }

      this._sendSealed(peer, { t: 'ack', ids: [frameId] });
    }

    switch (payload.t) {
      case 'ack': {
        for (const id of (payload.ids as string[]) ?? []) {
          const entry = peer.outbox.get(id);

          if (entry) {
            if (entry.timer) {
              clearTimeout(entry.timer);
            }

            peer.outbox.delete(id);
          }
        }

        return;
      }
      case 'rpc-res': {
        const rpcId = String(payload.id ?? '');
        const pending = this._pendingRpc.get(rpcId);

        if (!pending) {
          return;
        }

        clearTimeout(pending.timer);
        this._pendingRpc.delete(rpcId);

        if (payload.ok === true) {
          pending.resolve(payload.result);
        } else {
          const err = payload.error as { code?: string; message?: string } | undefined;
          pending.reject(new Error(err?.message || err?.code || 'Remote call failed'));
        }

        return;
      }
      case 'snapshot': {
        this._emit('snapshot', peer.deviceId, payload as unknown as Snapshot);
        return;
      }
      case 'event': {
        const event = String(payload.event ?? '');

        if (event === 'session.stream.batch') {
          this._emit('stream', peer.deviceId, payload.data as { sessionId: string; text: string; thinking: string });
        } else {
          this._emit('event', peer.deviceId, event, payload.data);
        }

        return;
      }
      case 'bye': {
        peer.session = null;
        this._emitStatus();

        return;
      }
      default:
        return;
    }
  }

  // ── outbound ─────────────────────────────────────────────────────────────

  /** Allowlisted RPC against one sidecar. */
  rpc<T = unknown>(sidecarId: string, method: string, params: object = {}, timeoutMs = RPC_TIMEOUT_MS): Promise<T> {
    const peer = this._peers.get(sidecarId);

    if (!peer?.session) {
      return Promise.reject(new Error('Mac is not reachable right now'));
    }

    const rpcId = crypto.randomUUID();

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pendingRpc.delete(rpcId);
        reject(new Error('Request timed out — the Mac may be offline'));
      }, timeoutMs);
      this._pendingRpc.set(rpcId, { resolve: resolve as (v: unknown) => void, reject, timer });
      this._sendReliable(peer, { t: 'rpc', id: rpcId, method, params });
    });
  }

  /** Toggle the per-session stream subscription. */
  subscribe(sidecarId: string, sessionId: string, on: boolean) {
    const peer = this._peers.get(sidecarId);

    if (peer?.session) {
      this._sendSealed(peer, { t: 'sub', sessionId, on });
    }
  }

  /** Report which session is foregrounded (null = tab hidden). */
  presence(sessionId: string | null) {
    for (const peer of this._peers.values()) {
      if (peer.session) {
        this._sendSealed(peer, { t: 'presence', sessionId });
      }
    }
  }

  private _sendEnvelope(to: string, sealed: { ct: string; n: string }) {
    if (this._ws?.readyState !== WebSocket.OPEN) {
      return;
    }

    this._ws.send(JSON.stringify({ v: 1, kind: 'frame', id: crypto.randomUUID(), to, ct: sealed.ct, n: sealed.n }));
  }

  private _sendSealed(peer: SidecarPeer, payload: object) {
    if (!peer.session) {
      return;
    }

    this._sendEnvelope(peer.deviceId, peer.session.seal(payload));
  }

  private _sendReliable(peer: SidecarPeer, payload: object) {
    const fid = crypto.randomUUID();
    const framed = { ...payload, fid };
    const entry = { payload: framed, attempts: 0, timer: null as ReturnType<typeof setTimeout> | null };
    peer.outbox.set(fid, entry);

    const attempt = () => {
      if (!peer.outbox.has(fid)) {
        return;
      }

      if (entry.attempts >= ACK_RETRY_MS.length + 1) {
        peer.outbox.delete(fid);
        return;
      }

      this._sendSealed(peer, framed);
      entry.timer = setTimeout(attempt, ACK_RETRY_MS[Math.min(entry.attempts, ACK_RETRY_MS.length - 1)]);
      entry.attempts += 1;
    };
    attempt();
  }

  private _teardownPeer(peer: SidecarPeer) {
    peer.session = null;
    peer.online = false;

    for (const e of peer.outbox.values()) {
      if (e.timer) {
        clearTimeout(e.timer);
      }
    }
    peer.outbox.clear();
  }
}

export const relay = new RelayClient();
